import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Grid,
  Typography,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { AppContext } from '../../context/Context';
import { useTranslation } from 'react-i18next';
import { SocketContext } from '../../context/SocketContext';

import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../../assets/css/buttons.css';
import {
  PointOfSale as PointOfSaleIcon,
  AddCard as AddCardIcon,
  ExitToApp as ExitToAppIcon,
  CalendarToday as CalendarTodayIcon,
  CameraAlt as CameraAltIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import WGLogo from '../../assets/img/wingames.png';
import CHLogo from '../../assets/img/ClubHouse_Logo_Light.png';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CashField from './CashField';
import { formatSignedCash } from '../../utils/formatCash';
import {
  centeredLabelSx,
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../theme/fieldTokens';
import ReceiptCapture from '../../components/ReceiptCapture';

// The two NT/Rikstoto hints. Brighter and a size up from fieldHelpSx: they name
// which line to copy off the terminal receipt, so they have to be read before
// the field is filled in — the rest of the help lines only matter afterwards
const readingHelpSx = {
  ...fieldHelpSx,
  color: '#c9d6f2',
  fontSize: '16px',
  fontWeight: 600,
  textAlign: 'center',
};

const draftKey = (hall) => `endShiftCount:${hall}`;

// Count step: fixed so the panel keeps one shape when tabs change section
const TABLE_HEIGHT = '53vh';

// Confirm step stacks table over summary, so 56vh overflowed the modal
const CONFIRM_TABLE_HEIGHT = '32vh';

// Past this the draft is from an earlier shift, not an interrupted count
const DRAFT_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const readDraft = (hall) => {
  try {
    const raw = window.localStorage.getItem(draftKey(hall));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDraft = (hall, draft) => {
  try {
    window.localStorage.setItem(draftKey(hall), JSON.stringify(draft));
  } catch {
    // A full or blocked storage must not stop a shift from closing
  }
};

const clearDraft = (hall) => {
  try {
    window.localStorage.removeItem(draftKey(hall));
  } catch {
    // as above
  }
};

function ModalEndShiftAuto() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    hall,
    operator,
    session,
    updateShowModalEndShift,
    updateIsDailyReportCompleted,
    updateIsCardReportCompleted,
    updateIsLoadingScreen,
    isLoadingScreen,
    printer,
    modifyLogin,
    modifyOperator,
    modifyOperatorFull,
    modifyHall,
    updateSession,
  } = useContext(AppContext);

  const hallRef = useRef(hall);

  useEffect(() => {
    hallRef.current = hall;
    socket.emit('checkShiftAndDayNumbers', {
      action: 'checkShiftAndDayNumbers',
      bingoName: hallRef.current,
      printer: printerRef.current,
    });
  }, [hall]);

  const isLoadingScreenRef = useRef(isLoadingScreen);
  useEffect(() => {
    isLoadingScreenRef.current = isLoadingScreen;
  }, [isLoadingScreen]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const { socket } = useContext(SocketContext);

  const [shiftNumbers, setShiftNumbers] = useState(null);
  // 0 I dag · 1 Skift — the day leads because it is what gets read first
  const [activeTab, setActiveTab] = useState(0);
  // 0 Totalt · 1 Kasse · 2 Selvbetjening — see SECTIONS
  const [activeSection, setActiveSection] = useState(0);
  const isDayTab = activeTab === 0;

  // Steps: 'choice' → 'count' (count, explain, sign) → 'confirm'; expected stays hidden until counted
  const [step, setStep] = useState('choice');
  // 'shift' → close shift only; 'day' → close shift + last of the day
  const [closeType, setCloseType] = useState(null);

  const [countedCash, setCountedCash] = useState('');
  const [avvikRevealed, setAvvikRevealed] = useState(false);
  const [avvikComment, setAvvikComment] = useState('');
  const [ntReading, setNtReading] = useState('');
  const [rikstotoReading, setRikstotoReading] = useState('');
  // Receipt pictures. Never required: a close cannot wait for a camera at 23:00
  const [receipts, setReceipts] = useState({ NT: null, Rikstoto: null });
  // Which provider's camera is open, if any
  const [capturing, setCapturing] = useState(null);
  const [signedBy, setSignedBy] = useState('');
  const [signedByID, setSignedByID] = useState('');
  // The figures the server refused with; it recomputes at confirm time and can disagree
  const [refusedWith, setRefusedWith] = useState(null);
  // Empty until the list arrives; until then the free-text field is what shows
  const [operators, setOperators] = useState([]);
  const [signConfirmed, setSignConfirmed] = useState(false);

  // A lone minus is a number still being typed, not a value
  const toNumber = (value) => {
    const n = Number(value);
    return value === '' || !Number.isFinite(n) ? null : n;
  };

  const countedNumber = toNumber(countedCash);
  const ntReadingNumber = toNumber(ntReading);
  const rikstotoReadingNumber = toNumber(rikstotoReading);

  // Both counters run per day, so a shift's share is the step since the last close
  // The server does the same arithmetic and owns it; this is only what is shown while counting
  const previousNt = shiftNumbers?.previousNt ?? null;
  const previousRikstoto = shiftNumbers?.previousRikstoto ?? null;

  // First close of the day: the counter started at zero, so the whole reading is this shift's
  const delta = (reading, previous) =>
    reading == null ? null : previous ? reading - previous.reading : reading;

  const ntNumber = delta(ntReadingNumber, previousNt);
  const rikstotoNumber = delta(rikstotoReadingNumber, previousRikstoto);

  // NT and Rikstoto run other software but the same drawer, so their net counts
  const externalNetto = (ntNumber ?? 0) + (rikstotoNumber ?? 0);
  const cashExpected =
    shiftNumbers?.cash?.expected == null
      ? null
      : shiftNumbers.cash.expected + externalNetto;

  // The day's counters are typed at this close too, so they ride on the day figure alike
  const dayExpected =
    shiftNumbers?.cash?.dayExpected == null
      ? null
      : shiftNumbers.cash.dayExpected + externalNetto;

  const countedAgainst =
    countedNumber != null && cashExpected != null
      ? countedNumber - cashExpected
      : null;

  // Once refused, the server's figure is the one on screen; its zero hides the comment field
  const avvik = refusedWith ? refusedWith.avvik : countedAgainst;
  const expectedShown = refusedWith ? refusedWith.cashExpected : cashExpected;

  // Mandatory every close: a blank is not a zero, it is an unanswered question
  const countInputsComplete =
    countedNumber != null &&
    ntReadingNumber != null &&
    rikstotoReadingNumber != null;

  // The employees offered in the signature select
  useEffect(() => {
    if (!socket || !hall) return;

    const handleOperators = (data) => {
      setOperators(Array.isArray(data?.operators) ? data.operators : []);
      if (data?.ok === false) {
        console.warn('listOperators unavailable:', data.reason);
      }
    };

    socket.on('listOperators', handleOperators);
    socket.emit('listOperators', {});
    return () => socket.off('listOperators', handleOperators);
  }, [socket, hall]);

  // Restore an unfinished count — values only, never the step
  const draftLoadedFor = useRef(null);
  useEffect(() => {
    if (!hall || draftLoadedFor.current === hall) return;
    draftLoadedFor.current = hall;

    const draft = readDraft(hall);
    if (!draft) return;

    // A draft older than a shift belongs to a count nobody is still doing
    if (!draft.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      clearDraft(hall);
      return;
    }

    setCountedCash(draft.countedCash ?? '');
    setAvvikComment(draft.avvikComment ?? '');
    setNtReading(draft.ntReading ?? '');
    setRikstotoReading(draft.rikstotoReading ?? '');
    setSignedBy(draft.signedBy ?? '');
    setSignedByID(draft.signedByID ?? '');

    if (draft.countedCash) toast(t('endShift.draftRestored'));
  }, [hall]);

  useEffect(() => {
    if (!hall || draftLoadedFor.current !== hall) return;
    if (!countedCash && !avvikComment && !signedBy && !signedByID) return;

    writeDraft(hall, {
      savedAt: Date.now(),
      countedCash,
      avvikComment,
      ntReading,
      rikstotoReading,
      signedBy,
      signedByID,
    });
  }, [
    hall,
    countedCash,
    avvikComment,
    ntReading,
    rikstotoReading,
    signedBy,
    signedByID,
  ]);

  useEffect(() => {
    const handleCloseShift = (data) => {
      updateIsLoadingScreen(false);

      if (!data) {
        toast.error(t('error.error'));
        return;
      }

      // The server recomputes expected from the rows, so a close can come back refused
      if (data.ok === false) {
        if (data.reason === 'avvikCommentRequired') {
          toast.error(t('endShift.avvikCommentRequired'));
          // Kept: the screen's own avvik would stay at zero and never render the field
          setRefusedWith({
            avvik: data.avvik ?? null,
            cashExpected: data.cashExpected ?? null,
          });
          setStep('count');
          setAvvikRevealed(true);
        } else if (data.reason === 'externalNettoRequired') {
          toast.error(t('endShift.externalNettoRequired'));
          setStep('count');
          setAvvikRevealed(false);
        } else if (data.reason === 'noOpenShift') {
          toast.error(t('endShift.noOpenShift'));
        } else {
          toast.error(t('error.error'));
        }
        return;
      }

      clearDraft(hallRef.current);
      modifyLogin(false);
      modifyOperator('');
      modifyOperatorFull('');
      modifyHall('');
      updateSession('');
      updateShowModalEndShift(false);
      navigate('/login');
    };

    const handleCheckShiftAndDayNumbers = (data) => {
      setShiftNumbers(data);
      updateIsLoadingScreen(false);
    };

    socket.on('closeShift', handleCloseShift);
    socket.on('checkShiftAndDayNumbers', handleCheckShiftAndDayNumbers);

    return () => {
      socket.off('closeShift', handleCloseShift);
      socket.off('checkShiftAndDayNumbers', handleCheckShiftAndDayNumbers);
    };
  }, [socket]);

  // Backing out abandons the count: leaving the draft would prefill the next one
  const handleCancel = () => {
    clearDraft(hallRef.current);
    updateShowModalEndShift(false);
  };

  const handleChoose = (type) => {
    setCloseType(type);
    setStep('count');
  };

  // Everything about one terminal in its own column: field, which line to copy,
  // its receipt and its netto. The netto lines used to sit under BOTH columns,
  // centred on the modal, so neither belonged to the reading above it
  const READINGS = {
    NT: {
      label: 'endShift.ntReading',
      help: 'endShift.ntReadingHelp',
      nettoLabel: 'endShift.ntNetto',
      value: () => ntReading,
      set: setNtReading,
      reading: () => ntReadingNumber,
      netto: () => ntNumber,
      previous: () => previousNt,
    },
    Rikstoto: {
      label: 'endShift.rikstotoReading',
      help: 'endShift.rikstotoReadingHelp',
      nettoLabel: 'endShift.rikstotoNetto',
      value: () => rikstotoReading,
      set: setRikstotoReading,
      reading: () => rikstotoReadingNumber,
      netto: () => rikstotoNumber,
      previous: () => previousRikstoto,
    },
  };

  const readingColumn = (provider) => {
    const r = READINGS[provider];
    const previous = r.previous();
    const label = t(r.nettoLabel);

    return (
      <Box sx={{ width: '280px' }}>
        <CashField
          label={t(r.label) + '*'}
          value={r.value()}
          onChange={r.set}
          width="280px"
          fontSize="20px"
          allowNegative
        />
        <Typography sx={readingHelpSx}>{t(r.help)}</Typography>
        {receiptButton(provider)}

        <Typography sx={{ ...fieldHelpSx, mt: 2 }}>
          {previous
            ? t('endShift.readingPrevious', {
                amount: formatSignedCash(previous.reading),
                date: previous.closedAt?.slice(0, 16) ?? '',
              })
            : t('endShift.readingNoPrevious')}
        </Typography>
        {/* Always on screen, em dash until there is a reading: appearing only
            once something is typed made the modal grow under the cursor */}
        <Typography sx={{ ...fieldHelpSx, mt: 0.5, fontWeight: 'bold' }}>
          {label}:{' '}
          {r.reading() == null ? '—' : `${formatSignedCash(r.netto() ?? 0)} kr`}
        </Typography>
      </Box>
    );
  };

  // Camera blue until the picture is in, then confirm green. `is-done` only
  // swaps the colour: the size stays the camera one, so the two sit level
  const receiptButton = (provider) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
      <button
        className={`btn-custom-camara${receipts[provider] ? ' is-done' : ''}`}
        onClick={() => setCapturing(provider)}
      >
        {receipts[provider] ? (
          <CheckCircleIcon style={{ fontSize: '1.2em' }} />
        ) : (
          <CameraAltIcon style={{ fontSize: '1.2em' }} />
        )}
        {receipts[provider]
          ? t('endShift.receiptAttached')
          : t('endShift.receiptTake')}
      </button>
    </Box>
  );

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('count');
      return;
    }
    setStep('choice');
    setCloseType(null);
  };

  // Reveal is one-way: "Tell på nytt" clears the figure and hides it again
  const handleReveal = () => {
    if (countedNumber == null) {
      toast.error(t('endShift.countMissing'));
      return;
    }
    if (ntReadingNumber == null || rikstotoReadingNumber == null) {
      toast.error(t('endShift.externalNettoRequired'));
      return;
    }
    setAvvikRevealed(true);
  };

  // Clears the external nets too: they feed expected, so keeping them is not a fresh start
  const handleRecount = () => {
    setCountedCash('');
    setNtReading('');
    setRikstotoReading('');
    setAvvikRevealed(false);
    // A new count is measured from scratch, so the refused figure no longer applies
    setRefusedWith(null);
  };

  const handleCountDone = () => {
    if (!countInputsComplete) {
      toast.error(t('endShift.countMissing'));
      return;
    }
    if (!avvikRevealed) {
      handleReveal();
      return;
    }
    if (avvik !== null && avvik !== 0 && !avvikComment.trim()) {
      toast.error(t('endShift.avvikCommentRequired'));
      return;
    }
    // With employees on file the id is the signature; without, the old text field is
    const signValid = operators.length ? signedByID !== '' : signedBy.trim();
    if (!signValid || !signConfirmed) {
      toast.error(t('endShift.signRequired'));
      return;
    }
    setStep('confirm');
  };

  const handleFinishConfirm = () => {
    updateIsLoadingScreen(true);
    socket.emit('closeShift', {
      action: 'closeShift',
      bingoName: hallRef.current,
      operator: operatorRef.current,
      session: sessionRef.current,
      sessionID: sessionRef.current,
      printer: printerRef.current,
      isLastOfTheDay: closeType === 'day',
      cashCounted: countedNumber,
      avvikComment: avvikComment.trim() || null,
      signedBy: signedBy.trim() || null,
      signedByID: signedByID === '' ? null : Number(signedByID),
      ntReading: ntReadingNumber,
      rikstotoReading: rikstotoReadingNumber,
      ntPhotoUrl: receipts.NT,
      rikstotoPhotoUrl: receipts.Rikstoto,
    });
  };

  const fmt = (v) =>
    v !== undefined && v !== null ? Number(v).toFixed(2) : '0.00';

  // Only the type scale is set here; colours and zebra come from the dark modal skin
  // Tripled parent: the skin's 22px/18px cells in buttons.css outrank a plain sx class
  const tableStyles = (compact) => ({
    '&&& .MuiTableCell-root': {
      fontSize: compact ? '12px' : '16px',
      lineHeight: 1.35,
      paddingTop: compact ? '1px' : '4px',
      paddingBottom: compact ? '1px' : '4px',
      paddingLeft: compact ? '8px' : '10px',
      paddingRight: compact ? '8px' : '10px',
      whiteSpace: 'nowrap',
    },
    '&&& .MuiTableCell-head': {
      fontSize: compact ? '10px' : '13px',
      paddingTop: compact ? '3px' : '4px',
      paddingBottom: compact ? '3px' : '4px',
    },
    // Three rules break the 18 rows into what they are: where the drawer started,
    // what it should hold, and what is only there to be read
    '&&& .row-opening .MuiTableCell-root': {
      paddingBottom: compact ? '4px' : '8px',
      borderBottom: '2px solid #2a3a6e',
    },
    '&&& .row-sum .MuiTableCell-root': {
      paddingTop: compact ? '4px' : '8px',
      borderTop: '2px solid #2a3a6e',
    },
    '&&& .row-expected .MuiTableCell-root': {
      fontSize: compact ? '14px' : '18px',
      color: '#ffffff',
    },
    '&&& .row-note-head .MuiTableCell-root': {
      paddingTop: compact ? '8px' : '14px',
      borderTop: '2px solid #2a3a6e',
    },
    '&&& .row-note .MuiTableCell-root': { color: '#8fa3d4' },
  });

  const cellSx = {};

  // Rows the operator can add up to reach Forventet; the informational ones sit below the total
  const renderProviderRows = (section, isKiosk = false, isDrawer = false) => {
    // CH and CH Spillerkonto are two halves of one provider, so every section shows their sum
    const chTotalData = {
      cashIn:
        (section?.ch?.cashIn ?? 0) + (section?.chSpillerkonto?.cashIn ?? 0),
      cashOut:
        (section?.ch?.cashOut ?? 0) + (section?.chSpillerkonto?.cashOut ?? 0),
      neto: (section?.ch?.neto ?? 0) + (section?.chSpillerkonto?.neto ?? 0),
    };

    // Own till, other software: the section's booked nettos plus this close, still unwritten
    const ntData = { neto: (section?.nt?.neto ?? 0) + (ntNumber ?? 0) };
    const rikstotoData = {
      neto: (section?.rikstoto?.neto ?? 0) + (rikstotoNumber ?? 0),
    };

    const providers = [
      { key: 'CH', data: section?.ch },
      { key: 'CH Spillerkonto', data: section?.chSpillerkonto },
      { key: 'OK D.', data: section?.ok },
      { key: 'WG', data: section?.wg },
      ...(!isKiosk ? [{ key: 'Kiosk', data: section?.kiosk }] : []),
      ...(!isKiosk ? [{ key: 'NT', data: ntData, external: true }] : []),
      ...(!isKiosk
        ? [
            {
              key: 'Rikstoto',
              data: rikstotoData,
              external: true,
            },
          ]
        : []),
    ];

    // Sales settled by card never reached the drawer, so the figure gives them back
    const deductions = isKiosk
      ? []
      : [
          {
            key: t('endShift.paymentCard'),
            cashOut: section?.payments?.card,
            neto: -(section?.payments?.card ?? 0),
          },
          {
            key: t('endShift.refunds'),
            cashOut: section?.refunds,
            neto: -(section?.refunds ?? 0),
          },
          {
            key: t('endShift.cashWithdrawalRow'),
            cashIn: section?.cashWithdrawal?.cashIn,
            cashOut: section?.cashWithdrawal?.cashOut,
            neto: section?.cashWithdrawal?.neto ?? 0,
          },
          {
            key: t('endShift.tillMovements'),
            cashIn: section?.till?.cashIn,
            cashOut: section?.till?.cashOut,
            neto: section?.till?.neto ?? 0,
          },
        ];

    // Same block on both tabs, each against its own opening balance
    const opening = isDayTab
      ? shiftNumbers?.cash?.dayOpening
      : shiftNumbers?.cash?.opening;
    const expected = isDayTab ? dayExpected : cashExpected;
    const showTotals = isDrawer && expected != null && opening != null;

    const line = (label, value, { bold, dim, group } = {}) => (
      <TableRow key={label} className={group}>
        <TableCell
          colSpan={3}
          sx={{
            ...cellSx,
            ...(bold && { fontWeight: 'bold' }),
            ...(dim && { color: '#8fa3d4' }),
          }}
        >
          {label}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            ...cellSx,
            fontWeight: 'bold',
            ...(dim && { color: '#8fa3d4' }),
          }}
        >
          {fmt(value)}
        </TableCell>
      </TableRow>
    );

    const movementRow = ({
      key,
      data,
      cashIn,
      cashOut,
      neto,
      external,
      group,
    }) => (
      <TableRow key={key} className={group}>
        <TableCell sx={cellSx}>{key}</TableCell>
        <TableCell align="right" sx={cellSx}>
          {external ? '-' : fmt(data ? data.cashIn : cashIn)}
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          {external ? '-' : fmt(data ? data.cashOut : cashOut)}
        </TableCell>
        <TableCell align="right" sx={{ ...cellSx, fontWeight: 'bold' }}>
          {fmt(data ? data.neto : neto)}
        </TableCell>
      </TableRow>
    );

    return (
      <>
        {showTotals &&
          line(t('endShift.openingCashRow'), opening, { group: 'row-opening' })}
        {providers.map(movementRow)}
        {deductions.map(movementRow)}
        {showTotals &&
          line(t('endShift.movementsSum'), expected - opening, {
            bold: true,
            group: 'row-sum',
          })}
        {showTotals &&
          line(t('endShift.expectedInTill'), expected, {
            bold: true,
            group: 'row-expected',
          })}

        {/* Below the total on purpose: both double-count rows already above them */}
        <TableRow key="informational" className="row-note-head">
          <TableCell colSpan={4} sx={{ ...cellSx, color: '#8fa3d4' }}>
            {t('endShift.informational')}
          </TableCell>
        </TableRow>
        {movementRow({
          key: 'CH Total',
          data: chTotalData,
          group: 'row-note',
        })}
        {!isKiosk &&
          movementRow({
            key: t('endShift.paymentCash'),
            cashIn: section?.payments?.cashIn,
            cashOut: section?.payments?.cashOut,
            neto:
              (section?.payments?.cashIn ?? 0) -
              (section?.payments?.cashOut ?? 0),
            group: 'row-note',
          })}
      </>
    );
  };

  const renderTableSection = (
    sectionData,
    label,
    isKiosk = false,
    height = TABLE_HEIGHT,
    compact = false,
    isDrawer = false
  ) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'bold',
            mb: 0.5,
            color: 'white',
            textAlign: 'center',
          }}
        >
          {label}
        </Typography>
      )}
      {/* overflowX so a narrow panel scrolls the table, not the modal */}
      <TableContainer
        sx={{ flex: 1, minHeight: 0, maxHeight: height, overflow: 'auto' }}
      >
        <Table stickyHeader sx={tableStyles(compact)}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="right">{t('endShift.cashin')}</TableCell>
              <TableCell align="right">{t('endShift.cashout')}</TableCell>
              <TableCell align="right">{t('endShift.neto')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderProviderRows(sectionData, isKiosk, isDrawer)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const periodData = isDayTab ? shiftNumbers?.day : shiftNumbers?.currentShift;

  // Which section is on screen; Totalt first and by default, it is the whole picture
  const SECTIONS = [
    { label: t('endShift.totalSection'), key: 'total', isKiosk: false },
    { label: t('endShift.cashierSection'), key: 'cashier', isKiosk: false },
    { label: t('endShift.selfKioskSection'), key: 'selfKiosk', isKiosk: true },
  ];

  const tabSx = {
    borderBottom: '1px solid #2a3a6e',
    minHeight: '48px',
    '& .MuiTab-root': {
      color: '#8fa3d4',
      // Touch panel: the MUI default is a 12px label on a 48px row
      fontSize: '17px',
      minHeight: '48px',
    },
    '& .MuiTab-root.Mui-selected': { color: 'white', fontWeight: 'bold' },
    '& .MuiTabs-indicator': { backgroundColor: '#fcad89' },
  };

  // Confirm step: two tab rows at 56px each ate the height the table needed
  const compactTabSx = {
    ...tabSx,
    minHeight: '42px',
    '& .MuiTab-root': {
      ...tabSx['& .MuiTab-root'],
      fontSize: '14px',
      minHeight: '42px',
    },
  };

  // Provider movements, shared by the count and confirm steps; shown only after the reveal
  const renderMovementsPanel = (compact = false) => {
    const section = SECTIONS[activeSection] ?? SECTIONS[0];
    const panelTabSx = compact ? compactTabSx : tabSx;

    return (
      <Paper
        elevation={2}
        sx={{
          mb: compact ? 1.5 : 2,
          overflow: 'hidden',
          backgroundColor: '#232a45',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          width: '100%',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ ...panelTabSx, backgroundColor: '#151d3b' }}
        >
          <Tab label={t('endShift.day')} />
          <Tab label={t('endShift.currentShift')} />
        </Tabs>
        <Tabs
          value={activeSection}
          onChange={(_, v) => setActiveSection(v)}
          variant="fullWidth"
          sx={{ ...panelTabSx, backgroundColor: '#1a2344' }}
        >
          {SECTIONS.map((s) => (
            <Tab key={s.key} label={s.label} />
          ))}
        </Tabs>
        <Box
          sx={{
            p: compact ? 1 : 1.5,
            flex: 1,
            minHeight: 0,
            display: 'flex',
          }}
        >
          {renderTableSection(
            periodData?.[section.key],
            null, // the tab already names it
            section.isKiosk,
            compact ? CONFIRM_TABLE_HEIGHT : TABLE_HEIGHT,
            compact,
            // Totalt is the drawer section, on the shift tab and on the day tab
            section.key === 'total'
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <>
      {isLoadingScreen ? (
        <></>
      ) : (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              {/* The camera takes over the whole modal: the count form has no room for a viewfinder */}
              {capturing && (
                <div
                  className="react-confirm-alert-body modal-status"
                  // Capped against the viewport: 700px on a 750px panel sits edge to edge
                  style={{ width: 'min(700px, 92vw)' }}
                >
                  <h1>{t('endShift.receiptTitle')}</h1>
                  <ReceiptCapture
                    title={
                      capturing === 'NT'
                        ? t('endShift.ntReading')
                        : t('endShift.rikstotoReading')
                    }
                    help={t('endShift.receiptHelp')}
                    filePrefix={`shift_${capturing.toLowerCase()}`}
                    onDone={(url) => {
                      setReceipts((prev) => ({ ...prev, [capturing]: url }));
                      setCapturing(null);
                    }}
                    onCancel={() => setCapturing(null)}
                  />
                </div>
              )}

              {/* ── STEP 1: CHOICE ── */}
              {!capturing && step === 'choice' && (
                <div
                  className="react-confirm-alert-body"
                  style={{ width: '700px' }}
                >
                  <h1>{t('endShift.choiceTitle')}</h1>
                  <p>{t('endShift.choiceSubtitle')}</p>
                  {/* btn-choice, not btn-custom-*: the latter carry
                      `width: 200px !important`, which beat the inline width and
                      left these three laid out at random. Same grid as
                      ModalCashMovement, so both choice screens read alike. */}
                  <div className="choice-grid">
                    <button
                      className="btn-choice tone-in"
                      onClick={() => handleChoose('shift')}
                    >
                      <ExitToAppIcon />
                      {t('endShift.closeShift')}
                    </button>
                    <button
                      className="btn-choice tone-out"
                      onClick={() => handleChoose('day')}
                    >
                      <CalendarTodayIcon />
                      {t('endShift.closeDay')}
                    </button>
                    <button
                      className="btn-choice tone-dismiss choice-wide"
                      onClick={handleCancel}
                    >
                      <CancelIcon />
                      {t('sales.cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2: COUNT THE DRAWER ── */}
              {!capturing && step === 'count' && (
                <div
                  className="react-confirm-alert-body modal-status"
                  // Narrow while counting, full panel once the tables are up
                  style={
                    avvikRevealed
                      ? {
                          // as in the confirm step: padding sits outside width
                          boxSizing: 'border-box',
                          width: '96vw',
                          maxWidth: '1900px',
                          // Safety net on a short panel: the modal blocks and has no Escape
                          // Fixed, not a cap: the panel must not resize when a tab
                          // with fewer rows is picked. The table flexes inside it
                          height: '88vh',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }
                      : { width: '820px' }
                  }
                >
                  <h1>{t('endShift.countTitle')}</h1>
                  <p>
                    {avvikRevealed
                      ? t('endShift.countSubtitle')
                      : t('endShift.countedHelp')}
                  </p>

                  {/* All three come from counting and reading, never from what
                      the till expects — so they are all committed before the
                      reveal. NT and Rikstoto feed the expected figure, so an
                      avvik cannot be worked out until they are in. */}
                  {!avvikRevealed && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '20px',
                        }}
                      >
                        <CashField
                          label={t('endShift.countedLabel') + '*'}
                          value={countedCash}
                          onChange={setCountedCash}
                          autoFocus
                        />
                      </div>

                      <Typography
                        sx={{
                          color: '#8fa3d4',
                          fontSize: '15px',
                          mt: 5,
                          mb: 1,
                          textAlign: 'center',
                        }}
                      >
                        {t('endShift.externalTillHelp')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          justifyContent: 'center',
                          mt: 3,
                        }}
                      >
                        {readingColumn('NT')}
                        {readingColumn('Rikstoto')}
                      </Box>
                    </>
                  )}

                  {!avvikRevealed && (
                    <div
                      className="react-confirm-alert-button-group"
                      style={{ marginTop: '32px' }}
                    >
                      <button
                        className="btn-custom-yes"
                        onClick={handleReveal}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                        {t('endShift.showAvvik')}
                      </button>
                      <button
                        className="btn-custom-back"
                        onClick={handleBack}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                        {t('endShift.back')}
                      </button>
                    </div>
                  )}

                  {avvikRevealed && (
                    <>
                      {/* Left: what is being signed. Right: where the money
                          went. Side by side because the comment cannot be
                          written without reading the movements, and making the
                          operator flip between two screens to do it is how
                          "annet avvik" ends up in every comment field. */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 3,
                          alignItems: 'stretch',
                          mt: 3,
                          textAlign: 'left',
                          flex: 1,
                          minHeight: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: '520px',
                            flexShrink: 0,
                            overflowY: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              mb: 2,
                              px: 3,
                              py: 2,
                              borderRadius: '14px',
                              backgroundColor: '#151d3b',
                              border: '1px solid #2a3a6e',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.5,
                                color: '#e6eaf2',
                                fontSize: '20px',
                                fontWeight: 600,
                              }}
                            >
                              <span>{t('endShift.countedLabel')}</span>
                              <span>{formatSignedCash(countedNumber)} kr</span>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.5,
                                color: '#8fa3d4',
                                fontSize: '20px',
                              }}
                            >
                              <span>{t('endShift.expected')}</span>
                              <span>
                                {expectedShown == null
                                  ? t('endShift.expectedUnknown')
                                  : `${formatSignedCash(expectedShown)} kr`}
                              </span>
                            </Box>

                            {/* Shown as their own lines so the operator can see
                                the two figures they typed inside the expected
                                total, rather than wondering where it moved */}
                            {[
                              [t('endShift.ntNetto'), ntNumber],
                              [t('endShift.rikstotoNetto'), rikstotoNumber],
                            ].map(([label, value]) => (
                              <Box
                                key={label}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  py: 0.25,
                                  pl: 2,
                                  color: '#6e82b4',
                                  fontSize: '16px',
                                }}
                              >
                                <span>{label}</span>
                                <span>{formatSignedCash(value ?? 0)} kr</span>
                              </Box>
                            ))}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                pt: 1,
                                mt: 1,
                                borderTop: '1px solid #2a3a6e',
                                fontSize: '30px',
                                fontWeight: 'bold',
                                // Green only when it balances; an avvik of any size must not look like a pass
                                color:
                                  avvik === null
                                    ? '#8fa3d4'
                                    : avvik === 0
                                      ? '#5ad19b'
                                      : '#fcad89',
                              }}
                            >
                              <span>{t('endShift.avvik')}</span>
                              <span>
                                {avvik === null
                                  ? '—'
                                  : `${avvik > 0 ? '+' : ''}${formatSignedCash(
                                      avvik
                                    )} kr`}
                              </span>
                            </Box>
                            {avvik !== null && (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  textAlign: 'right',
                                  color: '#8fa3d4',
                                  fontSize: '15px',
                                }}
                              >
                                {avvik === 0
                                  ? t('endShift.avvikNone')
                                  : avvik > 0
                                    ? t('endShift.avvikOver')
                                    : t('endShift.avvikUnder')}
                              </Box>
                            )}
                          </Box>

                          {avvik !== null && avvik !== 0 && (
                            <>
                              <TextField
                                autoComplete="off"
                                label={t('endShift.avvikCommentLabel') + '*'}
                                value={avvikComment}
                                onChange={(e) =>
                                  setAvvikComment(e.target.value)
                                }
                                variant="outlined"
                                multiline
                                maxRows={4}
                                inputProps={{ maxLength: 500 }}
                                InputLabelProps={{
                                  sx: verticallyCenteredLabelSx,
                                }}
                                className="modal-textfield"
                                style={{ width: '100%' }}
                              />
                              <Typography sx={fieldHelpSx}>
                                {t('endShift.avvikCommentHelp')}
                              </Typography>
                            </>
                          )}

                          {operators.length === 0 ? (
                            // The free-text field is the default and the fallback: an
                            // empty Select still opens a focus-trapping menu with
                            // nothing in it, which locks every other input on screen
                            <TextField
                              autoComplete="off"
                              label={t('endShift.signedByLabel') + '*'}
                              value={signedBy}
                              onChange={(e) => setSignedBy(e.target.value)}
                              variant="outlined"
                              inputProps={{
                                maxLength: 100,
                                style: { textAlign: 'center' },
                              }}
                              InputLabelProps={{ sx: centeredLabelSx }}
                              className="modal-textfield"
                              style={{ width: '340px' }}
                            />
                          ) : (
                            <TextField
                              select
                              label={t('endShift.signedByLabel') + '*'}
                              value={signedByID}
                              onChange={(e) => {
                                setSignedByID(e.target.value);
                                const picked = operators.find(
                                  (o) => o.id === Number(e.target.value)
                                );
                                setSignedBy(picked ? picked.name : '');
                              }}
                              variant="outlined"
                              InputLabelProps={{ sx: centeredLabelSx }}
                              className="modal-textfield"
                              style={{ width: '340px' }}
                              SelectProps={{
                                // The menu renders in a portal, so the modal skin
                                // cannot reach it — it carries its own colours
                                MenuProps: {
                                  // #react-confirm-alert is z-index 999999, the MUI
                                  // portal defaults to 1300: without this the menu
                                  // opens *behind* the modal, invisible but still
                                  // trapping focus, which locks every other field
                                  sx: { zIndex: 1000000 },
                                  PaperProps: {
                                    sx: {
                                      backgroundColor: '#151d3b',
                                      color: '#c9d6f2',
                                      maxHeight: 360,
                                      '& .MuiMenuItem-root': {
                                        fontSize: '20px',
                                        // Touch panel: 44px is below a reliable target
                                        minHeight: 56,
                                        justifyContent: 'center',
                                      },
                                      '& .MuiMenuItem-root.Mui-selected': {
                                        backgroundColor: '#28407a',
                                      },
                                    },
                                  },
                                },
                              }}
                              inputProps={{ style: { textAlign: 'center' } }}
                            >
                              {operators.map((o) => (
                                <MenuItem key={o.id} value={o.id}>
                                  {o.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                          <Typography sx={fieldHelpSx}>
                            {t('endShift.signedByHelp')}
                          </Typography>

                          <FormControlLabel
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              color: '#c9d6f2',
                              mb: 1,
                            }}
                            control={
                              <Checkbox
                                checked={signConfirmed}
                                onChange={(e) =>
                                  setSignConfirmed(e.target.checked)
                                }
                                sx={{
                                  color: '#8fa3d4',
                                  '&.Mui-checked': { color: '#5ad19b' },
                                  // Touch panel: the default box is a 20px target
                                  '& .MuiSvgIcon-root': { fontSize: 30 },
                                }}
                              />
                            }
                            label={t('endShift.signConfirm')}
                          />
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            display: 'flex',
                          }}
                        >
                          {renderMovementsPanel()}
                        </Box>
                      </Box>

                      <div className="react-confirm-alert-button-group">
                        <button
                          className="btn-custom-yes"
                          onClick={handleCountDone}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                          {t('sales.confirm')}
                        </button>
                        <button
                          className="btn-custom-postpone"
                          onClick={handleRecount}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                          {t('endShift.recount')}
                        </button>
                        <button
                          className="btn-custom-no"
                          onClick={handleCancel}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <CancelIcon style={{ fontSize: '1.2em' }} />
                          {t('sales.cancel')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP 3: CONFIRM ── */}
              {!capturing && step === 'confirm' && (
                <div
                  className="react-confirm-alert-body"
                  style={{
                    // Library padding sits outside width, so 96vw overflowed
                    boxSizing: 'border-box',
                    width: '92vw',
                    maxWidth: '1400px',
                    height: '88vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: '20px 24px',
                  }}
                >
                  <h1 style={{ fontSize: '24px' }}>{t('endShift.title')}</h1>
                  <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                    {t('endShift.subtitle')}
                  </p>
                  {renderMovementsPanel(true)}

                  {/* What is about to be signed, restated next to the numbers
                      it will be filed with. Last chance to go back. */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2.5,
                      justifyContent: 'center',
                      alignItems: 'baseline',
                      mb: 1.5,
                      px: 2,
                      py: 1,
                      borderRadius: '14px',
                      backgroundColor: '#151d3b',
                      border: '1px solid #2a3a6e',
                      color: '#c9d6f2',
                      fontSize: '16px',
                    }}
                  >
                    <span>
                      {t('endShift.countedLabel')}:{' '}
                      <strong>{formatSignedCash(countedNumber)} kr</strong>
                    </span>
                    <span>
                      {t('endShift.expected')}:{' '}
                      <strong>
                        {expectedShown == null
                          ? t('endShift.expectedUnknown')
                          : `${formatSignedCash(expectedShown)} kr`}
                      </strong>
                    </span>
                    <span
                      style={{
                        color:
                          avvik === null
                            ? '#8fa3d4'
                            : avvik === 0
                              ? '#5ad19b'
                              : '#fcad89',
                      }}
                    >
                      {t('endShift.avvik')}:{' '}
                      <strong>
                        {avvik === null
                          ? '—'
                          : `${avvik > 0 ? '+' : ''}${formatSignedCash(
                              avvik
                            )} kr`}
                      </strong>
                    </span>
                    <span>
                      {t('endShift.signedByLabel')}: <strong>{signedBy}</strong>
                    </span>
                  </Box>

                  <div className="react-confirm-alert-button-group">
                    <button
                      className="btn-custom-yes"
                      onClick={handleFinishConfirm}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.confirm')}
                    </button>
                    <button
                      className="btn-custom-back"
                      onClick={handleBack}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                      {t('endShift.back')}
                    </button>
                    <button
                      className="btn-custom-no"
                      onClick={handleCancel}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <CancelIcon style={{ fontSize: '1.2em' }} />
                      {t('sales.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ModalEndShiftAuto;
